import React, { useState } from 'react';
import Section from './Section';
import QuotesProposals from '../../components/forms/QuotesProposals';
import ComponentAwardBuilder from '../../components/forms/quotes/ComponentAwardBuilder';
import FinalDeliverablesCard from '../../components/forms/quotes/FinalDeliverablesCard';
import { AwardAllocation, QuoteComponentContext, QuoteComponentRecommendation } from '../../types/quote.types';

/**
 * /design?only=quotes — Wizard step 5 (Quotes & Proposals / award) states in
 * the v2 language: readiness strip + empty state (via the connected component
 * with a mock RFQ), an interactive award builder, generation progress, and
 * the deliverables card locked/unlocked.
 */

// No rfqId on purpose: the hook stays inert, so the gallery exercises the
// pre-generation surfaces (readiness strip + empty state) without a backend.
const MOCK_RFQ = {
  name: 'Warehouse AMR Drive Subsystem',
  boms: [{
    components: [{
      name: 'Hub Motor 48V',
      supplierCandidates: [
        { supplierName: 'Maxon Drive Systems', status: 'SHORTLISTED', conversations: [] },
        { supplierName: 'Nova Motion Works', status: 'SHORTLISTED', conversations: [] },
      ],
    }],
  }],
};

const COMPONENT: QuoteComponentContext = {
  componentId: 'comp-1',
  name: 'Hub Motor 48V',
  quantity: 4,
  targetPrice: 18000,
  candidates: [
    { supplierName: 'Maxon Drive Systems', quotedPrice: 17200, quoteSource: 'SUPPLIER_QUOTED', leadTimeWeeks: 9, trustScore: 94, contacted: true },
    { supplierName: 'Nova Motion Works', quotedPrice: 16400, quoteSource: 'RESEARCH', leadTimeWeeks: 6, trustScore: 88, contacted: true },
    { supplierName: 'Shenzhen Kinetix', quotedPrice: 15900, quoteSource: 'RESEARCH', leadTimeWeeks: 10, trustScore: 71 },
  ],
};

const RECOMMENDATION: QuoteComponentRecommendation = {
  componentId: 'comp-1',
  componentName: 'Hub Motor 48V',
  strategy: 'SPLIT_AWARD',
  reasoning: 'Maxon has the only committed quote and the strongest trust record; Nova hedges lead-time risk at a better estimated price. A 70/30 split keeps leverage for the next negotiation round.',
  negotiationTip: 'Maxon quoted 9 weeks — ask whether a 70% volume commitment moves them to 6.',
  allocations: [
    { supplierName: 'Maxon Drive Systems', sharePercent: 70, riskLevel: 'LOW', rationale: '', strengths: [], watchouts: [] },
    { supplierName: 'Nova Motion Works', sharePercent: 30, riskLevel: 'MEDIUM', rationale: '', strengths: [], watchouts: [] },
  ],
};

const noop = () => undefined;

const InteractiveAwardBuilder: React.FC = () => {
  const [allocations, setAllocations] = useState<AwardAllocation[]>(
    RECOMMENDATION.allocations.map(a => ({ supplierName: a.supplierName, sharePercent: a.sharePercent })),
  );
  const setShare = (supplierName: string, sharePercent: number) => {
    setAllocations(prev => {
      const without = prev.filter(a => a.supplierName !== supplierName);
      return [...without, { supplierName, sharePercent }].filter(a => a.sharePercent > 0 || supplierName === a.supplierName);
    });
  };
  return (
    <ComponentAwardBuilder
      component={COMPONENT}
      aiRecommendation={RECOMMENDATION}
      allocations={allocations}
      onChangeShare={setShare}
      onResetToAI={() => setAllocations(RECOMMENDATION.allocations.map(a => ({ supplierName: a.supplierName, sharePercent: a.sharePercent })))}
      index={0}
      disabled={false}
    />
  );
};

const QuotesGallery: React.FC = () => (
  <>
    <Section title="Wizard step 5 — readiness strip + empty state (estimate-only)">
      <div className="mx-auto max-w-content">
        <QuotesProposals rfq={MOCK_RFQ} onBack={noop} />
      </div>
    </Section>
    <Section title="Wizard step 5 — award builder (interactive)">
      <div className="mx-auto flex max-w-content flex-col gap-s7">
        <InteractiveAwardBuilder />
      </div>
    </Section>
    <Section title="Wizard step 5 — deliverables locked · unlocked">
      <div className="mx-auto flex max-w-content flex-col gap-s7">
        <FinalDeliverablesCard isUnlocked={false} downloading={null} onDownload={noop} awardedSuppliers={[]} onDownloadSupplierRfq={noop} />
        <FinalDeliverablesCard
          isUnlocked={true}
          downloading={null}
          onDownload={noop}
          awardedSuppliers={['Shenzhen Drivetech Co.', 'Pune Servo Systems']}
          onDownloadSupplierRfq={noop}
        />
      </div>
    </Section>
  </>
);

export default QuotesGallery;
